import {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import testService from '@/services/testService';

import languageCodes from '../../../../consts/localization/languageCodes';
import routes from '../../../../consts/routes';
import themeStyle from '../../../../consts/themeStyle';
import {getConfig} from '../../../../services/config/configService';
import downloadService from '../../../../services/downloadService';
import localizationControllerService from '../../../../services/localization/localizationControllerService';
import loyaltyService from '../../../../services/loyalty/loyaltyService';
import serviceWorkerService from '../../../../services/serviceWorkerService';
import storageService from '../../../../services/storageService';
import themeService from '../../../../services/themeService';
import uamClientService from '../../../../services/uam/uamClientService';
import {
    selectConsentsFlagsValues,
    selectIsFirmwareMock,
    selectIsIncludeEmptyPuff,
} from '../../../../state/selectors/session';
import {setIsUseSystemTheme} from '../../../../state/slice/global';
import {setIsFirmwareMock, setIsIncludeEmptyPuff} from '../../../../state/slice/session';
import {copyToBuffer} from '../../../../utils/bufferUtils';
import SettingsRow from './SettingsRow';
import SettingsSection from './SettingsSection';
import SettingsToggleRow from './SettingsToggleRow';

const SettingsTab = ({onOpenSegments, onClose}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const consentsFlagsValues = useSelector(selectConsentsFlagsValues);
    const isFirmwareMock = useSelector(selectIsFirmwareMock);
    const isIncludeEmptyPuff = useSelector(selectIsIncludeEmptyPuff);
    const [isBrowserCheckDisabled, setIsBrowserCheckDisabled] = useState(storageService.getIsBrowserCheckDisabled());
    const [isVisibilityReconnectDisabled, setIsVisibilityReconnectDisabled] = useState(
        storageService.getIsVisibilityReconnectDisabled()
    );
    const [isGeoRestrictionEnabled, setIsGeoRestrictionEnabled] = useState(
        storageService.getGeoRestrictionFromLocalStorage()
    );

    const handleLanguageChange = async (languageCode) => {
        await localizationControllerService.switchLocalization(languageCode);
    };

    const handleThemeChange = async (theme) => {
        themeService.applyTheme(theme);
        dispatch(setIsUseSystemTheme(false));
        storageService.setThemeToLocalStorage(theme);
    };

    const handleConsentsReset = () => {
        const updatedConsents = Object.fromEntries(Object.keys(consentsFlagsValues).map((key) => [key, false]));

        uamClientService.putUserConsents(updatedConsents);
    };

    const handleFirmwareMockToggle = () => {
        dispatch(setIsFirmwareMock(!isFirmwareMock));
    };

    const handleEmptyPuffsToggle = () => {
        dispatch(setIsIncludeEmptyPuff(!isIncludeEmptyPuff));
    };

    const handleBrowserCheckToggle = () => {
        const newValue = !isBrowserCheckDisabled;

        setIsBrowserCheckDisabled(newValue);

        if (newValue) {
            storageService.setIsBrowserCheckDisabled();
        } else {
            storageService.removeIsBrowserCheckDisabled();
        }
    };

    const handleVisibilityReconnectToggle = () => {
        const newValue = !isVisibilityReconnectDisabled;

        setIsVisibilityReconnectDisabled(newValue);
        storageService.setIsVisibilityReconnectDisabled(newValue);
    };

    const handleGeoRestrictionToggle = () => {
        const newValue = !isGeoRestrictionEnabled;

        setIsGeoRestrictionEnabled(newValue);

        if (newValue) {
            storageService.setGeoRestrictionToLocalStorage('false');
        } else {
            storageService.removeGeoRestrictionFromLocalStorage();
        }
    };

    const handleSubscriptionCopy = async () => {
        const swReg = await serviceWorkerService.registerServiceWorker();
        const subscription = await swReg.pushManager.getSubscription();

        copyToBuffer(JSON.stringify(subscription));
    };

    const handleOpenDevTools = () => {
        onClose();
        navigate(routes.DEV_TOOLS);
    };

    const handlePrintLocalizationConfig = () => {
        const config = getConfig();

        console.log('Localization Config:', JSON.stringify(config?.marketLocalizations, null, 2));
    };

    return (
        <div className='flex flex-col gap-4'>
            <SettingsSection title='Appearance'>
                <SettingsRow
                    label='Dark Mode'
                    actions={[{text: 'Apply', onClick: () => handleThemeChange(themeStyle.DARK)}]}
                />
                <SettingsRow
                    label='Light Mode'
                    actions={[{text: 'Apply', onClick: () => handleThemeChange(themeStyle.LIGHT)}]}
                />
            </SettingsSection>

            <SettingsSection title='Localization'>
                <SettingsRow
                    label='English'
                    actions={[
                        {text: 'Save', onClick: () => downloadService.downloadDefaultLocalization(languageCodes.EN)},
                        {text: 'Switch', onClick: () => handleLanguageChange(languageCodes.EN)},
                    ]}
                />
                <SettingsRow
                    label='Czech'
                    actions={[
                        {text: 'Save', onClick: () => downloadService.downloadDefaultLocalization(languageCodes.CS)},
                        {text: 'Switch', onClick: () => handleLanguageChange(languageCodes.CS)},
                    ]}
                />
                <SettingsRow
                    label='Localization Config'
                    actions={[{text: 'Print', onClick: handlePrintLocalizationConfig}]}
                />
            </SettingsSection>

            <SettingsSection title='Toggles'>
                <SettingsToggleRow
                    label='Geo Restriction Disabled'
                    isActive={isGeoRestrictionEnabled}
                    onClick={handleGeoRestrictionToggle}
                />
                <SettingsToggleRow
                    label='Mock Firmware Update'
                    isActive={isFirmwareMock}
                    onClick={handleFirmwareMockToggle}
                />
                <SettingsToggleRow
                    label='Include Empty Puffs'
                    isActive={isIncludeEmptyPuff}
                    onClick={handleEmptyPuffsToggle}
                />
                <SettingsToggleRow
                    label='Browser Compatibility'
                    isActive={!isBrowserCheckDisabled}
                    onClick={handleBrowserCheckToggle}
                />
                <SettingsToggleRow
                    label='Visibility Reconnect Disabled'
                    isActive={isVisibilityReconnectDisabled}
                    onClick={handleVisibilityReconnectToggle}
                />
            </SettingsSection>

            <SettingsSection title='Actions'>
                <SettingsRow label='Device' actions={[{text: 'Switch', onClick: () => testService.switchDevice()}]} />
                <SettingsRow label='Consents' actions={[{text: 'Clear', onClick: handleConsentsReset}]} />
                <SettingsRow label='Subscription Info' actions={[{text: 'Copy', onClick: handleSubscriptionCopy}]} />
                <SettingsRow
                    label='Loyalty Badges'
                    actions={[{text: 'Get', onClick: () => loyaltyService.getBadgeData()}]}
                />
                <SettingsRow label='Loyalty Segments' actions={[{text: 'Change', onClick: onOpenSegments}]} />
                <SettingsRow label='Dev Tools' actions={[{text: 'Open', onClick: handleOpenDevTools}]} />
            </SettingsSection>
        </div>
    );
};

export default SettingsTab;
